import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Chip,
  Breadcrumbs,
  Link,
  LinearProgress,
} from '@weave-mui/material';
import FormLabel, { formLabelVariants } from '@weave-mui/form-label';

import CloseIcon from '@mui/icons-material/Close';

import LoadingSpinner from 'components/LoadingSpinner';
import PageTitle from 'components/PageTitle';
import WeaveMuiThemeProvider from 'components/WeaveMuiThemeProvider';
import WeaveConfirmationModal from 'components/WeaveConfirmationModal';
import { addNewNotification } from 'store/actions/notification_actions';
import { removeHostingProject } from 'store/actions/hosting_actions';
import {
  getHostingProjects,
  postHostingNewProject,
  putHostingEditProject,
  getGitBranches,
} from 'utils/api.service';

/**
 * Validation schema for Project form using Yup
 * Validates required fields and email formats
 */
const validationSchema = Yup.object().shape({
  displayname: Yup.string()
    .trim()
    .required('Display name is required')
    .max(100, 'Display name must be 100 characters or less'),

  projectname: Yup.string()
    .trim()
    .max(50, 'Project name must be 50 characters or less')
    .matches(
      /^[a-zA-Z0-9_-]+$/,
      'Project name can only contain letters, numbers, hyphens, and underscores'
    ),

  teamname: Yup.string().required('Team name is required'),

  awsaccountid: Yup.string().required('AWS account is required'),

  ubuntuimageid: Yup.string()
    .trim()
    .max(100, 'AWS Ubuntu OS imageID must be 100 characters or less'),

  windowsimageid: Yup.string()
    .trim()
    .max(100, 'AWS Windows OS imageId must be 100 characters or less'),

  tools: Yup.string().required('Build tool is required'),

  emaildl: Yup.string()
    .trim()
    .email('Please enter a valid email address')
    .max(100, 'Email must be 100 characters or less'),

  owneremail: Yup.string()
    .trim()
    .email('Please enter a valid email address')
    .max(100, 'Email must be 100 characters or less'),

  gitdirpath: Yup.string()
    .trim()
    .max(200, 'Windows project path must be 200 characters or less'),

  ubuntugitdirpath: Yup.string()
    .trim()
    .max(200, 'Ubuntu project path must be 200 characters or less'),

  gitbranchname: Yup.string()
    .trim()
    .max(100, 'Git branch must be 100 characters or less'),

  status: Yup.string().required('Status is required'),

  securitygroup: Yup.string()
    .trim()
    .max(100, 'Security group must be 100 characters or less'),
});

/**
 * ProjectForm - Component for creating or editing hosting projects
 *
 * This component uses Formik and Yup for form state management and validation.
 * It fetches project metafields from the API to populate select options.
 *
 * @param {Object} props
 * @param {'create' | 'edit'} props.mode - Form mode (defaults to 'create')
 * @returns {JSX.Element} The project form page
 */
function ProjectForm({ mode = 'create' }) {
  const history = useHistory();
  const { category, projectname: projectnameParam } = useParams();
  const dispatch = useDispatch();

  const menu = useSelector((state) => state.app.menu);
  const service = menu.service;
  const workstream = menu[service];
  const workstreamName = menu.categoryName;
  const org = service;

  const isEditMode = mode === 'edit';

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [metafields, setMetafields] = useState([]);
  const [branches, setBranches] = useState([]);
  const [supportedEnvironments, setSupportedEnvironments] = useState([]);
  const [newEnvironment, setNewEnvironment] = useState('');
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Get teamname options from metafields
   */
  const getTeamnameOptions = useCallback(() => {
    const teamnameField = metafields.find((f) => f.field === 'teamname');
    if (teamnameField && teamnameField.options) {
      return teamnameField.options.map((option) => {
        if (option.teamname && option.displayteamname) {
          return { id: option.teamname, name: option.displayteamname };
        }
        return { id: option, name: option };
      });
    }
    return [];
  }, [metafields]);

  /**
   * Get AWS account options based on selected teamname
   */
  const getAwsAccountOptions = useCallback(
    (teamname) => {
      const awsField = metafields.find((f) => f.field === 'awsaccountid');
      if (awsField && awsField.options && teamname) {
        const accounts = awsField.options[teamname] || [];
        return accounts.map((account) => ({
          id: account.id,
          name: account.name,
        }));
      }
      return [];
    },
    [metafields]
  );

  /**
   * Get git repo URL from branches based on team and AWS account
   */
  const getGitRepoFromBranches = useCallback(
    (teamname, awsaccountid) => {
      if (!branches || branches.length === 0 || !teamname || !awsaccountid) {
        return '';
      }

      const matchingBranch = branches.find(
        (branch) =>
          branch.teamname === teamname && branch.awsaccountid === awsaccountid
      );

      if (matchingBranch && matchingBranch.gitrepo) {
        return matchingBranch.gitrepo.trim().replace(/\.git$/, '');
      }

      return '';
    },
    [branches]
  );

  /**
   * Fetch project metafields and branches on mount
   * If in edit mode, also fetch the project to edit
   */
  useEffect(() => {
    const fetchData = async () => {
      if (!org || !workstream) {
        return;
      }

      setIsLoading(true);
      try {
        const [projectsResponse, branchesResponse] = await Promise.all([
          getHostingProjects(org, workstream),
          getGitBranches(org, workstream),
        ]);

        setMetafields(projectsResponse.data.metafield || []);
        setBranches(branchesResponse.data || []);

        // If in edit mode, find and set the project to edit
        if (isEditMode && projectnameParam) {
          const project = projectsResponse.data.project.find(
            (p) => p.projectname === projectnameParam
          );

          if (project) {
            setProjectToEdit(project);
            // Set supported environments from project's envlist
            if (project.envlist && Array.isArray(project.envlist)) {
              const envs = project.envlist.map((env) =>
                env.displayname.toUpperCase()
              );
              setSupportedEnvironments(envs);
            }

            // Auto-populate gitrepourl if empty
            if (!project.gitrepourl) {
              const gitRepoFromBranches = branchesResponse.data.find(
                (branch) =>
                  branch.teamname === project.teamname &&
                  branch.awsaccountid === project.awsaccountid
              );
              if (gitRepoFromBranches && gitRepoFromBranches.gitrepo) {
                project.gitrepourl = gitRepoFromBranches.gitrepo
                  .trim()
                  .replace(/\.git$/, '');
              }
            }
          } else {
            dispatch(addNewNotification('Project not found', 'danger'));
            history.push(`/hosting/${category}`);
          }
        }
      } catch (error) {
        console.error('Error fetching project data:', error);
        dispatch(
          addNewNotification('Failed to load project configuration', 'danger')
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    org,
    workstream,
    dispatch,
    isEditMode,
    projectnameParam,
    category,
    history,
  ]);

  /**
   * Get initial form values - memoized to prevent form resets
   */
  const initialValues = useMemo(() => {
    // If in edit mode and project is loaded, use project data
    if (isEditMode && projectToEdit) {
      return {
        displayname: projectToEdit.displayname || '',
        projectname: projectToEdit.projectname || '',
        teamname: projectToEdit.teamname || '',
        awsaccountid: projectToEdit.awsaccountid || '',
        ubuntuimageid: projectToEdit.ubuntuimageid || '',
        windowsimageid: projectToEdit.windowsimageid || '',
        tools: projectToEdit.tools || 'maven',
        emaildl: projectToEdit.emaildl || '',
        owneremail: projectToEdit.owneremail || '',
        gitdirpath: projectToEdit.gitdirpath || '',
        ubuntugitdirpath: projectToEdit.ubuntugitdirpath || '',
        gitbranchname: projectToEdit.gitbranchname || '',
        status: projectToEdit.status || 'enabled',
        securitygroup: projectToEdit.securitygroup || '',
        gitrepourl: projectToEdit.gitrepourl || '',
        // Add any conditional fields from the project
        ...(projectToEdit.ubuntuImageId && {
          ubuntuImageId: projectToEdit.ubuntuImageId,
        }),
        ...(projectToEdit.windowsImageId && {
          windowsImageId: projectToEdit.windowsImageId,
        }),
        ...(projectToEdit.owneremail && {
          owneremail: projectToEdit.owneremail,
        }),
        ...(projectToEdit.securitygroupid && {
          securitygroupid: projectToEdit.securitygroupid,
        }),
      };
    }

    // Create mode - use defaults
    const teamnameOptions = getTeamnameOptions();
    const initialTeamname =
      teamnameOptions.length > 0 ? teamnameOptions[0].id : '';
    const awsOptions = getAwsAccountOptions(initialTeamname);
    const initialAwsAccount = awsOptions.length > 0 ? awsOptions[0].id : '';

    // Get default status from metafields
    const statusField = metafields.find((f) => f.field === 'status');
    const initialStatus = statusField?.default || 'enabled';

    // Get default tools from metafields
    const toolsField = metafields.find((f) => f.field === 'tools');
    const initialTools = toolsField?.default || 'maven';

    // Build initial values object
    const initialVals = {
      displayname: '',
      projectname: '',
      teamname: initialTeamname,
      awsaccountid: initialAwsAccount,
      ubuntuimageid: '',
      windowsimageid: '',
      tools: initialTools,
      emaildl: '',
      owneremail: '',
      gitdirpath: '',
      ubuntugitdirpath: '',
      gitbranchname: '',
      status: initialStatus,
      securitygroup: '',
      gitrepourl: getGitRepoFromBranches(initialTeamname, initialAwsAccount),
    };

    // Add default values for conditional fields based on initial tool
    if (toolsField?.fields?.[initialTools]) {
      toolsField.fields[initialTools].forEach((condField) => {
        if (condField.default) {
          initialVals[condField.field] = condField.default;
        }
      });
    }

    // Set default gitdirpath for playwright (from MetafieldsModal logic)
    if (initialTools === 'playwright') {
      initialVals.gitdirpath = 'C:\\';
    }

    return initialVals;
  }, [
    isEditMode,
    projectToEdit,
    metafields,
    branches,
    getTeamnameOptions,
    getAwsAccountOptions,
    getGitRepoFromBranches,
  ]);

  /**
   * Handle adding a new supported environment
   */
  const handleAddEnvironment = () => {
    if (
      newEnvironment.trim() &&
      !supportedEnvironments.includes(newEnvironment.trim().toUpperCase())
    ) {
      setSupportedEnvironments([
        ...supportedEnvironments,
        newEnvironment.trim().toUpperCase(),
      ]);
      setNewEnvironment('');
    }
  };

  /**
   * Handle removing a supported environment
   */
  const handleRemoveEnvironment = (envToRemove) => {
    setSupportedEnvironments(
      supportedEnvironments.filter((env) => env !== envToRemove)
    );
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (values, { setSubmitting }) => {
    setIsSubmitting(true);
    try {
      // Build the payload
      const payload = {
        org,
        workstreamname: workstream,
        ...values,
        envlist: supportedEnvironments.map((env) => ({
          displayname: env,
          env: env,
        })),
        supportedenv: supportedEnvironments,
      };

      // Add default values for playwright hidden fields
      if (values.tools === 'playwright') {
        payload.ubuntuImageId = 'latest';
        payload.windowsImageId = 'latest';
        payload.gitdirpath = 'C:\\';
      }

      // Add default values for uft tool
      if (values.tools === 'uft') {
        payload.ubuntuImageId = 'latest';
      }

      // Find displayteamname from teamname options
      const teamnameOptions = getTeamnameOptions();
      const selectedTeam = teamnameOptions.find(
        (t) => t.id === values.teamname
      );
      if (selectedTeam) {
        payload.displayteamname = selectedTeam.name;
      }

      let response;
      if (isEditMode) {
        // Edit existing project
        response = await putHostingEditProject(
          projectToEdit.projectname,
          payload
        );
      } else {
        // Create new project
        response = await postHostingNewProject(payload);
      }

      dispatch(
        addNewNotification(
          response.data.message ||
            (isEditMode
              ? 'Project updated successfully'
              : 'Project created successfully'),
          'success'
        )
      );

      // Navigate back to hosting page and request refresh (project list changed)
      history.push({
        pathname: `/hosting/${category}`,
        state: { refreshHosting: true },
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        (isEditMode ? 'Failed to update project' : 'Failed to create project');
      dispatch(addNewNotification(errorMessage, 'danger'));
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  /**
   * Handle cancel button click
   */
  const handleCancel = () => {
    history.push(`/hosting/${category}`);
  };

  /**
   * Handle delete project
   */
  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await dispatch(
        removeHostingProject(workstream, projectToEdit.projectname)
      );
      dispatch(addNewNotification('Project deleted successfully', 'success'));
      history.push({
        pathname: `/hosting/${category}`,
        state: { refreshHosting: true },
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to delete project';
      dispatch(addNewNotification(errorMessage, 'danger'));
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
  };

  const teamnameOptions = getTeamnameOptions();

  /**
   * Render a single input field based on field type
   * Similar to MetafieldsModal.renderInput
   */
  const renderInput = useCallback(
    (field, formikProps) => {
      const {
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
      } = formikProps;
      const value = values[field.field] || '';

      // Get the tools field configuration from metafields
      const toolsField = metafields.find((f) => f.field === 'tools');
      const selectedTool = values.tools;

      // Check if this field should be shown based on selected tool
      // Fields defined in tools.fields[selectedTool] are conditional
      if (toolsField && toolsField.fields && selectedTool) {
        const toolSpecificFields = toolsField.fields[selectedTool] || [];
        const toolFieldNames = toolSpecificFields.map((f) => f.field);

        // Check if this is a conditional field (defined in any tool's fields)
        const allConditionalFields = Object.values(toolsField.fields)
          .flat()
          .map((f) => f.field);

        if (allConditionalFields.includes(field.field)) {
          // This is a conditional field - only show if it's in the current tool's fields
          if (!toolFieldNames.includes(field.field)) {
            return null;
          }
        }
      }

      // Hide specific fields when BUILD_TOOL is 'playwright' (from MetafieldsModal logic)
      const buildTool = values.tools;
      const playwrightHiddenFields = [
        'ubuntuimageid',
        'windowsimageid',
        'gitdirpath',
        'ubuntugitdirpath',
      ];

      if (
        buildTool === 'playwright' &&
        playwrightHiddenFields.includes(field.field.toLowerCase())
      ) {
        return null;
      }

      let input = null;

      switch (field.type) {
        case 'string':
        case 'number':
        case 'date':
          // Determine input type
          const inputType =
            field.field === 'owneremail' || field.field === 'emaildl'
              ? 'email'
              : 'text';

          input = (
            <Field name={field.field}>
              {({ field: formikField, meta }) => (
                <TextField
                  {...formikField}
                  label={field.name || field.field}
                  fullWidth
                  variant="outlined"
                  type={inputType}
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                  placeholder={
                    field.default || `Enter ${field.name || field.field}`
                  }
                  disabled={isEditMode && field.field === 'projectname'}
                />
              )}
            </Field>
          );
          break;

        case 'select':
          if (!field.options) {
            return null;
          }

          let fieldOptions = field.options;

          // Handle teamname field structure
          if (field.field === 'teamname') {
            input = (
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                error={touched.teamname && Boolean(errors.teamname)}
              >
                <FormLabel htmlFor="teamname" variant={formLabelVariants.TOP}>
                  {field.name || 'Team name'}
                </FormLabel>
                <Select
                  id="teamname"
                  name="teamname"
                  value={values.teamname}
                  onChange={(e) => {
                    const newTeamname = e.target.value;
                    setFieldValue('teamname', newTeamname);
                    // Reset AWS account when team changes
                    const awsOptions = getAwsAccountOptions(newTeamname);
                    const newAwsAccount =
                      awsOptions.length > 0 ? awsOptions[0].id : '';
                    setFieldValue('awsaccountid', newAwsAccount);
                    // Update git repo URL
                    const gitRepoUrl = getGitRepoFromBranches(
                      newTeamname,
                      newAwsAccount
                    );
                    if (gitRepoUrl) {
                      setFieldValue('gitrepourl', gitRepoUrl);
                    }
                  }}
                  onBlur={handleBlur}
                  variant="outlined"
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {teamnameOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.teamname && errors.teamname && (
                  <FormHelperText>{errors.teamname}</FormHelperText>
                )}
              </FormControl>
            );
          } else if (field.field === 'awsaccountid') {
            // Handle AWS account field
            input = (
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                error={touched.awsaccountid && Boolean(errors.awsaccountid)}
              >
                <FormLabel
                  htmlFor="awsaccountid"
                  variant={formLabelVariants.TOP}
                >
                  {field.name || 'AWS account'}
                </FormLabel>
                <Select
                  id="awsaccountid"
                  name="awsaccountid"
                  value={values.awsaccountid}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {getAwsAccountOptions(values.teamname).map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.awsaccountid && errors.awsaccountid && (
                  <FormHelperText>{errors.awsaccountid}</FormHelperText>
                )}
              </FormControl>
            );
          } else if (field.field === 'tools') {
            // Handle Build tool field - options from metafields
            const toolOptions = Array.isArray(field.options)
              ? field.options
              : [];
            input = (
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                error={touched.tools && Boolean(errors.tools)}
              >
                <FormLabel htmlFor="tools" variant={formLabelVariants.TOP}>
                  {field.name || 'Build tool'}
                </FormLabel>
                <Select
                  id="tools"
                  name="tools"
                  value={values.tools}
                  onChange={(e) => {
                    const newTool = e.target.value;
                    setFieldValue('tools', newTool);

                    // Set default values for conditional fields based on selected tool
                    if (field.fields && field.fields[newTool]) {
                      field.fields[newTool].forEach((toolField) => {
                        if (toolField.default) {
                          setFieldValue(toolField.field, toolField.default);
                        }
                      });
                    }

                    // Set default gitdirpath for playwright (from MetafieldsModal logic)
                    if (newTool === 'playwright') {
                      setFieldValue('gitdirpath', 'C:\\');
                    }
                  }}
                  onBlur={handleBlur}
                  variant="outlined"
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {toolOptions.map((option) => {
                    const optionValue =
                      typeof option === 'string'
                        ? option
                        : option.id || option.value;
                    const optionLabel =
                      typeof option === 'string'
                        ? option
                        : option.name || option.label || optionValue;
                    return (
                      <MenuItem key={optionValue} value={optionValue}>
                        {optionLabel}
                      </MenuItem>
                    );
                  })}
                </Select>
                {touched.tools && errors.tools && (
                  <FormHelperText>{errors.tools}</FormHelperText>
                )}
              </FormControl>
            );
          } else if (field.field === 'status') {
            // Handle Status field - options from metafields
            const statusOptions = Array.isArray(field.options)
              ? field.options
              : [];
            input = (
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                error={touched.status && Boolean(errors.status)}
              >
                <FormLabel htmlFor="status" variant={formLabelVariants.TOP}>
                  {field.name || 'Status'}
                </FormLabel>
                <Select
                  id="status"
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {statusOptions.map((option) => {
                    const optionValue =
                      typeof option === 'string'
                        ? option
                        : option.id || option.value;
                    const optionLabel =
                      typeof option === 'string'
                        ? option
                        : option.name || option.label || optionValue;
                    return (
                      <MenuItem key={optionValue} value={optionValue}>
                        {optionLabel}
                      </MenuItem>
                    );
                  })}
                </Select>
                {touched.status && errors.status && (
                  <FormHelperText>{errors.status}</FormHelperText>
                )}
              </FormControl>
            );
          } else {
            // Generic select field
            const options = Array.isArray(fieldOptions) ? fieldOptions : [];
            input = (
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                error={touched[field.field] && Boolean(errors[field.field])}
              >
                <FormLabel
                  htmlFor={field.field}
                  variant={formLabelVariants.TOP}
                >
                  {field.name || field.field}
                </FormLabel>
                <Select
                  id={field.field}
                  name={field.field}
                  value={value}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {options.map((option) => {
                    const optionId =
                      typeof option === 'string'
                        ? option
                        : option.id || option.value;
                    const optionName =
                      typeof option === 'string'
                        ? option
                        : option.name || option.label;
                    return (
                      <MenuItem key={optionId} value={optionId}>
                        {optionName}
                      </MenuItem>
                    );
                  })}
                </Select>
                {touched[field.field] && errors[field.field] && (
                  <FormHelperText>{errors[field.field]}</FormHelperText>
                )}
              </FormControl>
            );
          }
          break;

        case 'list':
          // Handle list type fields, specifically supportedenv
          if (field.field === 'supportedenv') {
            input = (
              <FormControl fullWidth variant="outlined">
                <FormLabel
                  htmlFor="supportedenv"
                  variant={formLabelVariants.TOP}
                >
                  {field.name || 'Supported test environment'}
                </FormLabel>
                <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                  <TextField
                    id="supportedenv"
                    value={newEnvironment}
                    onChange={(e) => setNewEnvironment(e.target.value)}
                    placeholder="Enter environment (e.g., STG, PRD)"
                    variant="outlined"
                    fullWidth
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddEnvironment();
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddEnvironment}
                    sx={{ minWidth: 80 }}
                  >
                    Add
                  </Button>
                </Box>
                <FormHelperText
                  sx={{
                    marginTop: '4px !important',
                    marginBottom: '8px !important',
                    fontSize: '12px',
                    color: 'text.secondary',
                    fontWeight: 400,
                  }}
                >
                  Click add to save an element
                </FormHelperText>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {supportedEnvironments.map((env) => (
                    <Chip
                      key={env}
                      label={env}
                      onDelete={() => handleRemoveEnvironment(env)}
                      size="small"
                      deleteIcon={<CloseIcon sx={{ width: 24, height: 24 }} />}
                      sx={{
                        minWidth: 70,
                        height: 24,
                        fontSize: '12px',
                        fontWeight: 600,
                        borderRadius: '4px',
                      }}
                    />
                  ))}
                </Box>
              </FormControl>
            );
          }
          break;

        default:
          return null;
      }

      return input ? <Box sx={{ mb: 2 }}>{input}</Box> : null;
    },
    [
      metafields,
      teamnameOptions,
      getAwsAccountOptions,
      getGitRepoFromBranches,
      newEnvironment,
      supportedEnvironments,
      handleAddEnvironment,
      handleRemoveEnvironment,
      isEditMode,
    ]
  );

  /**
   * Render fields organized into rows with 2 columns
   * Similar to MetafieldsModal.renderFields
   */
  const renderFields = useCallback(
    (formikProps) => {
      if (!metafields || metafields.length === 0) {
        return null;
      }

      // Get all fields to render including ALL conditional fields from ALL tools
      const toolsField = metafields.find((f) => f.field === 'tools');

      // Collect all possible conditional fields from all tools
      const allConditionalFields = [];
      if (toolsField?.fields) {
        Object.values(toolsField.fields).forEach((toolFields) => {
          toolFields.forEach((condField) => {
            // Only add if not already in the list and not in metafields
            const existsInMetafields = metafields.find(
              (f) => f.field === condField.field
            );
            const existsInConditional = allConditionalFields.find(
              (f) => f.field === condField.field
            );
            if (!existsInMetafields && !existsInConditional) {
              allConditionalFields.push(condField);
            }
          });
        });
      }

      // Merge metafields with all conditional fields in the correct order
      const allFields = [];

      metafields.forEach((field) => {
        allFields.push(field);

        // If this is the tools field, insert ALL conditional fields right after it
        if (field.field === 'tools' && allConditionalFields.length > 0) {
          allConditionalFields.forEach((condField) => {
            allFields.push(condField);
          });
        }
      });

      const fieldRows = [];
      let currentRow = [];

      allFields.forEach((field, index) => {
        const renderedField = renderInput(field, formikProps);
        const shouldShow = renderedField !== null;

        // Always add the field to maintain consistent layout, but control visibility
        currentRow.push({
          field,
          renderedField: shouldShow ? renderedField : null,
          shouldShow,
        });

        // If row is full (2 items) or it's the last field, add to rows
        if (currentRow.length === 2 || index === allFields.length - 1) {
          fieldRows.push([...currentRow]);
          currentRow = [];
        }
      });

      // Handle any remaining items in currentRow
      if (currentRow.length > 0) {
        fieldRows.push([...currentRow]);
      }

      return fieldRows.map((row, rowIndex) => (
        <React.Fragment key={`row-${rowIndex}`}>
          {row.map((item, colIndex) => {
            if (item.fullWidth) {
              return (
                <Grid
                  item
                  xs={12}
                  key={`${item.field.field}-${colIndex}`}
                  sx={{ display: item.shouldShow ? 'block' : 'none' }}
                >
                  {item.renderedField}
                </Grid>
              );
            }
            return (
              <Grid
                item
                xs={12}
                md={6}
                key={`${item.field.field}-${colIndex}`}
                sx={{ display: item.shouldShow ? 'block' : 'none' }}
              >
                {item.renderedField}
              </Grid>
            );
          })}
        </React.Fragment>
      ));
    },
    [metafields, renderInput]
  );

  if (isLoading) {
    return (
      <WeaveMuiThemeProvider>
        <Box sx={{ p: 3 }}>
          <LoadingSpinner text="Loading project configuration..." />
        </Box>
      </WeaveMuiThemeProvider>
    );
  }

  return (
    <WeaveMuiThemeProvider>
      <Box sx={{ p: 3, maxWidth: 1200 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }} separator="/">
          <Link
            underline="hover"
            color="text.secondary"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              history.push('/');
            }}
          >
            Home
          </Link>
          <Link
            underline="hover"
            color="text.secondary"
            href={`/hosting/${category}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/hosting/${category}`);
            }}
          >
            Test hosting
          </Link>
          <Link
            underline="hover"
            color="text.secondary"
            href={`/hosting/${category}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/hosting/${category}`);
            }}
          >
            {workstreamName}
          </Link>
          <Typography color="text.primary">
            {isEditMode ? 'Edit project' : 'Add a project'}
          </Typography>
        </Breadcrumbs>

        {/* Page Title */}
        <PageTitle
          title={
            isEditMode
              ? `Edit project${
                  projectToEdit?.displayname
                    ? ` - ${projectToEdit.displayname}`
                    : ''
                }`
              : 'Add a project'
          }
        />

        {/* Progress Bar - shown while submitting */}
        {isSubmitting && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Form */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={isEditMode}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isValid,
          }) => {
            return (
              <Form>
                {/* Scrollable form fields container */}
                <Box
                  sx={{
                    maxHeight: 'calc(100vh - 300px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    pr: 1,
                    mr: -1,
                  }}
                >
                  <Grid container spacing={2} alignItems="flex-start">
                    {/* Dynamically rendered fields from metafields */}
                    {renderFields({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      setFieldValue,
                    })}
                  </Grid>
                </Box>

                {/* Action Buttons - Fixed at bottom */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 2.5,
                    px: 3,
                    mx: -3,
                    mb: -3,
                    mt: 3,
                    backgroundColor: 'background.paper',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    paddingTop: 2,
                  }}
                >
                  {/* Delete button (edit mode only) */}
                  {isEditMode && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleDeleteClick}
                      disabled={isSubmitting}
                      sx={{
                        borderColor: '#d32f2f',
                        color: '#d32f2f',
                        '&:hover': {
                          borderColor: '#c62828',
                          backgroundColor: 'rgba(211, 47, 47, 0.04)',
                        },
                      }}
                    >
                      Delete project
                    </Button>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      ml: isEditMode ? 'auto' : 0,
                      marginLeft: 'auto',
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={
                        isSubmitting ||
                        !isValid ||
                        supportedEnvironments.length === 0
                      }
                      color="primary"
                    >
                      {isSubmitting
                        ? isEditMode
                          ? 'Saving...'
                          : 'Adding...'
                        : isEditMode
                        ? 'Save changes'
                        : 'Add project'}
                    </Button>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>

        {/* Delete Confirmation Modal */}
        <WeaveConfirmationModal
          open={showDeleteDialog}
          title="Delete Project"
          message={`Do you want to delete the project "${projectToEdit?.displayname}"? This action cannot be undone.`}
          confirmButtonText="Delete"
          onConfirm={handleDeleteConfirm}
          loading={isDeleting}
          loadingText="Deleting..."
          onClose={handleDeleteCancel}
        />
      </Box>
    </WeaveMuiThemeProvider>
  );
}

export default ProjectForm;
